import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Switch,
} from "@mui/material";
import { Edit, Delete, FileDownload, Add } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import axios from "axios";

import config from "../../config";
import useLayoutStore from "../../store/useLayoutStore";
import CustomTable from "../../components/common/CustomTable";
import CustomSearch from "../../components/common/CustomSearch";
import CustomPaging from "../../components/common/CustomPaging";
import CustomButton from "../../components/common/CustomButton";
import CouponForm from "./CouponForm"; // Component giống CmsForm nhưng cho Coupon

const drawerWidth = 240;
const collapsedWidth = 80;

const CouponList = () => {
  const { t } = useTranslation();
  const { isSidebarOpen } = useLayoutStore();

  const [couponList, setCouponList] = useState([]);
  const [allCoupons, setAllCoupons] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [editDialog, setEditDialog] = useState({ open: false, data: null });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${config.apiBaseUrl}/coupons`);
      const filtered = res.data.filter(
        (c) =>
          !searchQuery ||
          c.code?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setAllCoupons(filtered);
      const start = (page - 1) * itemsPerPage;
      const paginated = filtered.slice(start, start + itemsPerPage);
      setCouponList(paginated);
    } catch (error) {
      console.error(error);
      showSnackbar(t("Failed to load coupons"), "error");
    } finally {
      setLoading(false);
    }
  }, [page, itemsPerPage, searchQuery, t]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const handleEditSubmit = async () => {
    const { data } = editDialog;
    try {
      if (data.couponId) {
        await axios.put(`${config.apiBaseUrl}/coupons/${data.couponId}`, data);
        showSnackbar(t("Coupon updated successfully"));
      } else {
        await axios.post(`${config.apiBaseUrl}/coupons`, data);
        showSnackbar(t("Coupon created successfully"));
      }
      setEditDialog({ open: false, data: null });
      fetchCoupons();
    } catch (error) {
      console.error(error);
      showSnackbar(t("Failed to save coupon"), "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("Are you sure you want to delete this coupon?")))
      return;
    try {
      await axios.delete(`${config.apiBaseUrl}/coupons/${id}`);
      showSnackbar(t("Coupon deleted successfully"));
      fetchCoupons();
    } catch (error) {
      console.error(error);
      showSnackbar(t("Failed to delete coupon"), "error");
    }
  };

  const exportToExcel = () => {
    const exportData = allCoupons.map((item) => ({
      ID: item.couponId,
      [t("Code")]: item.code,
      [t("Discount %")]: item.discountPercentage,
      [t("Status")]: item.status,
      [t("Start Date")]: new Date(item.startDate).toLocaleDateString("vi-VN"),
      [t("End Date")]: new Date(item.endDate).toLocaleDateString("vi-VN"),
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "CouponList");
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buffer]), "DanhSachVoucher.xlsx");
    showSnackbar(t("Exported to Excel"));
  };

  const couponColumns = [
    { field: "couponId", label: "ID", minWidth: "80px" },
    { field: "code", label: t("Code"), minWidth: "120px" },
    { field: "discountPercentage", label: t("Discount %"), minWidth: "100px" },
    { field: "status", label: t("Status"), minWidth: "100px" },
    {
      field: "startDate",
      label: t("Start Date"),
      minWidth: "120px",
      customRender: (row) =>
        new Date(row.startDate).toLocaleDateString("vi-VN"),
    },
    {
      field: "endDate",
      label: t("End Date"),
      minWidth: "120px",
      customRender: (row) => new Date(row.endDate).toLocaleDateString("vi-VN"),
    },
    {
      field: "actions",
      label: t("Actions"),
      minWidth: "160px",
      customRender: (row) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title={t("Edit")}>
            <IconButton
              onClick={() => setEditDialog({ open: true, data: row })}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("Delete")}>
            <IconButton
              color="error"
              onClick={() => handleDelete(row.couponId)}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: { xs: 1.5, md: 2 },
        width: "100%",
        minWidth: 0, // QUAN TRỌNG
        height: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflowX: "hidden", // chặn tràn ngang
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          {t("Coupon Management")}
        </Typography>
        <CustomButton
          text={t("Create New Coupon")}
          startIcon={<Add />}
          onClick={() => setEditDialog({ open: true, data: {} })}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <CustomSearch
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
        <CustomButton
          text={t("Export")}
          startIcon={<FileDownload />}
          onClick={exportToExcel}
        />
      </Box>

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <CircularProgress />
        </Box>
      ) : couponList.length > 0 ? (
        <CustomTable columns={couponColumns} data={couponList} />
      ) : (
        <Typography align="center" mt={5}>
          {t("No coupons available")}
        </Typography>
      )}

      <CustomPaging
        page={page}
        setPage={setPage}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        totalItems={allCoupons.length}
      />

      <CouponForm
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, data: null })}
        data={editDialog.data}
        onChange={(key, value) =>
          setEditDialog((prev) => ({
            ...prev,
            data: { ...prev.data, [key]: value },
          }))
        }
        onSubmit={handleEditSubmit}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CouponList;
